import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";

import { useLoading } from "context/LoadingContext";
import { useNotification } from "context/NotificationContext";

import Api from "utils/Api";

const EnderecoFuncionario = ({ 
  dados, 
  setDados, 
  errors = {}, 
  obrigatorios = [],
  isDisabled = false 
}) => {
  const { setLoading } = useLoading();
  const requisicao = new Api();
  const notify = useNotification();

  const isRequired = (campo) => obrigatorios.includes(campo);
  const isError = (campo) => errors && errors[campo];

  const [cep, setCep] = useState(dados.cep || "");

  // Sincronizar estado interno do CEP com dados externos
  useEffect(() => {
    if (dados.cep && dados.cep !== cep) {
      setCep(dados.cep);
    }
  }, [dados.cep, cep]);

  const buscarEnderecoPorCep = async (cepLimpo) => {
    try {
      setLoading(true);
      const data = await requisicao.Get({
        url: `https://viacep.com.br/ws/${cepLimpo}/json/`
      });
      
      if (data.uf) {
        setDados(prev => ({
          ...prev,
          logradouro: data.logradouro || "",
          bairro: data.bairro || "",
          cidade: data.localidade || "",
          estado: data.uf || ""
        }));
      } else {
        notify({
          type: "error",
          message: "CEP não encontrado"
        });
      }
    } catch (error) {
      notify({
        type: "error",
        message: "Erro ao buscar CEP"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCepChange = (e) => {
    const { value } = e.target;
    setCep(value);
    setDados(prev => ({ ...prev, cep: value }));
  };

  const handleCepBlur = (e) => {
    const { value } = e.target;
    const cepLimpo = value.replace(/\D/g, "");
    if (cepLimpo.length === 8) {
      buscarEnderecoPorCep(cepLimpo);
    }
  };

  const handleFieldChange = (field, value) => {
    setDados(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="row g-3">
      {/* Primeira linha: CEP, Logradouro, Número */}
      <div className="col-md-3">
        <label htmlFor="cep" className="form-label">
          CEP {isRequired("cep") && <span className="text-danger">*</span>}
        </label>
        <InputMask
          id="cep"
          name="cep"
          value={cep}
          onChange={handleCepChange}
          onBlur={handleCepBlur}
          className={`form-control ${isError("cep") ? "is-invalid" : ""}`}
          mask="99999-999"
          placeholder="00000-000"
          disabled={isDisabled}
        />
        {isError("cep") && <div className="invalid-feedback">CEP é obrigatório</div>}
      </div>

      <div className="col-md-6">
        <label htmlFor="logradouro" className="form-label">
          Logradouro {isRequired("logradouro") && <span className="text-danger">*</span>}
        </label>
        <InputText
          id="logradouro"
          name="logradouro"
          value={dados.logradouro || ""}
          onChange={(e) => handleFieldChange("logradouro", e.target.value)}
          className={`form-control ${isError("logradouro") ? "is-invalid" : ""}`}
          placeholder="Rua, Avenida, etc."
          disabled={isDisabled}
        />
        {isError("logradouro") && <div className="invalid-feedback">Logradouro é obrigatório</div>}
      </div>

      <div className="col-md-3">
        <label htmlFor="numero" className="form-label">
          Número {isRequired("numero") && <span className="text-danger">*</span>}
        </label>
        <InputText
          id="numero"
          name="numero"
          value={dados.numero || ""}
          onChange={(e) => handleFieldChange("numero", e.target.value)}
          className={`form-control ${isError("numero") ? "is-invalid" : ""}`}
          placeholder="123"
          disabled={isDisabled}
        />
        {isError("numero") && <div className="invalid-feedback">Número é obrigatório</div>}
      </div>

      {/* Segunda linha: Complemento, Bairro */}
      <div className="col-md-6">
        <label htmlFor="complemento" className="form-label">
          Complemento
        </label>
        <InputText
          id="complemento"
          name="complemento"
          value={dados.complemento || ""}
          onChange={(e) => handleFieldChange("complemento", e.target.value)}
          className="form-control"
          placeholder="Apto, Sala, etc."
          disabled={isDisabled}
        />
      </div>

      <div className="col-md-6">
        <label htmlFor="bairro" className="form-label">
          Bairro {isRequired("bairro") && <span className="text-danger">*</span>}
        </label>
        <InputText
          id="bairro"
          name="bairro"
          value={dados.bairro || ""}
          onChange={(e) => handleFieldChange("bairro", e.target.value)}
          className={`form-control ${isError("bairro") ? "is-invalid" : ""}`}
          placeholder="Centro, Vila Nova, etc."
          disabled={isDisabled}
          readOnly
        />
        {isError("bairro") && <div className="invalid-feedback">Bairro é obrigatório</div>}
      </div>

      {/* Terceira linha: Cidade, Estado */}
      <div className="col-md-6">
        <label htmlFor="cidade" className="form-label">
          Cidade {isRequired("cidade") && <span className="text-danger">*</span>}
        </label>
        <InputText
          id="cidade"
          name="cidade"
          value={dados.cidade || ""}
          onChange={(e) => handleFieldChange("cidade", e.target.value)}
          className={`form-control ${isError("cidade") ? "is-invalid" : ""}`}
          placeholder="São Paulo"
          disabled={isDisabled}
          readOnly
        />
        {isError("cidade") && <div className="invalid-feedback">Cidade é obrigatória</div>}
      </div>

      <div className="col-md-6">
        <label htmlFor="estado" className="form-label">
          Estado {isRequired("estado") && <span className="text-danger">*</span>}
        </label>
        <InputText
          id="estado"
          name="estado"
          value={dados.estado || ""}
          onChange={(e) => handleFieldChange("estado", e.target.value)}
          className={`form-control ${isError("estado") ? "is-invalid" : ""}`}
          placeholder="SP"
          maxLength={2}
          disabled={isDisabled}
          readOnly
        />
        {isError("estado") && <div className="invalid-feedback">Estado é obrigatório</div>}
      </div>
    </div>
  );
};

EnderecoFuncionario.propTypes = {
  dados: PropTypes.shape({
    cep: PropTypes.string,
    logradouro: PropTypes.string,
    numero: PropTypes.string,
    complemento: PropTypes.string,
    bairro: PropTypes.string,
    cidade: PropTypes.string,
    estado: PropTypes.string
  }).isRequired,
  setDados: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(PropTypes.bool),
  obrigatorios: PropTypes.arrayOf(PropTypes.string),
  isDisabled: PropTypes.bool
};

EnderecoFuncionario.defaultProps = {
  errors: {},
  obrigatorios: [],
  isDisabled: false
};

export default EnderecoFuncionario;